import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const SlickCarouselContext = createContext();

export const SlickCarouselProvider = ({ settings, className, children }) => {
    const sliderRef = useRef(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(true);

    const afterChange = useCallback((current) => {
        if (!sliderRef.current) return;
        const totalSlides = React.Children.count(sliderRef.current.props.children);
        setCanScrollPrev(settings.infinite || current > 0);
        setCanScrollNext(settings.infinite || current < totalSlides - 1);
    }, [settings.infinite]);

    const scrollPrev = useCallback(() => sliderRef.current?.slickPrev(), []);
    const scrollNext = useCallback(() => sliderRef.current?.slickNext(), []);

    const contextValue = {
        sliderRef,
        settings: { ...settings, afterChange },
        canScrollPrev,
        canScrollNext,
        scrollPrev,
        scrollNext,
    };

    return (
        <div className={`${className}`}>
            <SlickCarouselContext.Provider value={contextValue}>
                {children}
            </SlickCarouselContext.Provider>
        </div>
    );
};


export const useSlickCarousel = () => {
    const context = useContext(SlickCarouselContext);
    if (!context) {
        throw new Error('useSlickCarousel must be used within a <SlickCarouselProvider />');
    }
    return context;
};

export const CarouselPrevious = ({ customButton }) => {
    const { scrollPrev, canScrollPrev } = useSlickCarousel();

    if (!customButton) return null; // No button rendered if no custom button is provided

    return React.cloneElement(customButton, { onClick: scrollPrev, disabled: !canScrollPrev });
};

export const CarouselNext = ({ customButton }) => {
    const { scrollNext, canScrollNext } = useSlickCarousel();

    if (!customButton) return null; // No button rendered if no custom button is provided

    return React.cloneElement(customButton, { onClick: scrollNext, disabled: !canScrollNext });
};

export const Carousel = ({ children }) => {
    const { sliderRef, settings } = useSlickCarousel();
    return <Slider ref={sliderRef} {...settings}>{children}</Slider>;
};

export const CarouselItem = ({ children, className = '' }) => (
    <div className={`slick-item ${className}`}>{children}</div>
);

export default Carousel;
